/**
 * Meeting template steps as the editor reads and writes them.
 *
 * Kept free of Vue and `@/` imports so meetingTemplateSteps.test.mjs runs
 * under plain Node.
 */

/**
 * A step of /api/v1/meeting-templates. The executed copy on a focus-group
 * result (`steps[]`) adds rounds_used, completed_by and completion_reason.
 *
 * @typedef {Object} TemplateStep
 * @property {string} step_id
 * @property {number} order
 * @property {string} step_name
 * @property {string} facilitator_prompt
 * @property {string|null} persona_prompt
 * @property {number} max_rounds -1 = dynamic, > 0 = that many fixed rounds
 * @property {string|null} facilitator_completion_condition
 * @property {boolean} show_attachment all of the run's stimulus materials are visible during the step (default true)
 */

// `show_attachment` defaults to true on the backend, also for steps stored
// before the field existed: only an explicit false hides the run's stimulus
// materials, all of them together, during the step.
export function stepShowsAttachment(step) {
  return step?.show_attachment !== false;
}

// The backend refuses attachments for a template where no step shows them.
export function templateShowsAttachment(template) {
  return Array.isArray(template?.steps) && template.steps.some(stepShowsAttachment);
}

// A stored step as the editor holds it: optional texts as '' for the inputs,
// show_attachment always a boolean for the switch.
export function editableTemplateStep(step) {
  return {
    ...step,
    persona_prompt: step.persona_prompt ?? '',
    facilitator_completion_condition: step.facilitator_completion_condition ?? '',
    show_attachment: stepShowsAttachment(step),
  };
}

// The `steps` of a template POST/PATCH, numbered by position. They replace the
// whole stored array and an omitted show_attachment comes back as true, so
// every step must carry its own value or a stored false is lost.
export function templateStepsPayload(steps) {
  return steps.map((s, i) => ({
    ...(s.step_id ? { step_id: s.step_id } : {}),
    order: i + 1,
    step_name: s.step_name.trim(),
    facilitator_prompt: s.facilitator_prompt.trim(),
    persona_prompt: (s.persona_prompt || '').trim() ? s.persona_prompt.trim() : null,
    max_rounds: s.max_rounds ?? -1,
    facilitator_completion_condition: (s.facilitator_completion_condition || '').trim()
      ? s.facilitator_completion_condition.trim()
      : null,
    show_attachment: stepShowsAttachment(s),
  }));
}
